/* eslint-disable react-hooks/set-state-in-effect */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
    Target, Calendar, TrendingUp, MessageSquareQuote, ChevronDown, ChevronUp,
    Sparkles,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import api from '../../lib/api';

/**
 * DirectorMissionCard — Affiche la mission Providence en cours assignée
 * par le PDG du groupe au directeur d'agence (rôle admin).
 *
 * Affiché sur le AdminDashboard (TAB overview). Masqué si pas de mission active.
 */
export default function DirectorMissionCard() {
    const [missions, setMissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get('/director/missions/active');
                if (!cancelled) setMissions(res.data?.items || []);
            } catch (err) {
                // 403 si non-admin, 401 si pas de token — on masque silencieusement
                if (!cancelled) setMissions([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (loading || missions.length === 0) return null;

    const formatDate = (iso) => {
        if (!iso) return '';
        try {
            return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
        } catch { return ''; }
    };

    return (
        <div className="space-y-3" data-testid="director-mission-card">
            {missions.map((m) => {
                const isExpanded = expandedId === m.id;
                const atRisk = m.progression_pct < 30 && m.days_remaining < (m.duration_days || 30) / 2;
                const elapsed = (m.duration_days || 0) - (m.days_remaining || 0);

                return (
                    <motion.div
                        key={m.id}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <Card className="bg-gradient-to-br from-[#681793]/15 via-slate-900 to-[#FF914D]/8 border-[#681793]/40 overflow-hidden">
                            <CardContent className="p-5">
                                {/* Header */}
                                <div className="flex items-start justify-between gap-3 mb-4">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center shrink-0">
                                            <Target className="w-5 h-5 text-white" />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <Badge className="bg-[#681793]/20 text-[#FF914D] border-[#FF914D]/30 hover:bg-[#681793]/30 text-[10px] uppercase tracking-wider font-bold">
                                                    Mission Providence
                                                </Badge>
                                                {atRisk && (
                                                    <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px] uppercase">
                                                        À risque
                                                    </Badge>
                                                )}
                                            </div>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                Confiée par votre dirigeant · J+{elapsed}/{m.duration_days}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-2xl font-bold text-white leading-none">
                                            {m.current_score}<span className="text-sm text-muted-foreground">/{m.target_score}</span>
                                        </div>
                                        <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1">
                                            début : {m.initial_score}
                                        </div>
                                    </div>
                                </div>

                                {/* Objective */}
                                <div className="mb-4">
                                    <p className="text-[10px] uppercase tracking-wider text-[#FF914D]/70 font-bold mb-1.5">
                                        Objectif principal
                                    </p>
                                    <p className="text-base text-white font-medium leading-snug">
                                        {m.objective_main}
                                    </p>
                                </div>

                                {/* Progress */}
                                <div className="mb-4">
                                    <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-xs text-muted-foreground">Progression</span>
                                        <span className="text-xs font-semibold text-white">{m.progression_pct}%</span>
                                    </div>
                                    <Progress
                                        value={m.progression_pct}
                                        className="h-1.5 bg-slate-800"
                                    />
                                </div>

                                {/* Quick meta */}
                                <div className="grid grid-cols-3 gap-3 mb-3 pb-3 border-b border-border/40">
                                    <div className="flex items-center gap-2">
                                        <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                                        <div>
                                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Reste</p>
                                            <p className="text-sm font-semibold text-white">{m.days_remaining}j</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <TrendingUp className="w-3.5 h-3.5 text-muted-foreground" />
                                        <div>
                                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Cible</p>
                                            <p className="text-sm font-semibold text-white">{m.target_score}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <MessageSquareQuote className="w-3.5 h-3.5 text-muted-foreground" />
                                        <div>
                                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Synthèses</p>
                                            <p className="text-sm font-semibold text-white">{m.synthesis_count}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Latest synthesis */}
                                {m.last_synthesis ? (
                                    <div>
                                        <button
                                            type="button"
                                            onClick={() => setExpandedId(isExpanded ? null : m.id)}
                                            className="flex items-center gap-2 text-xs text-[#FF914D] hover:text-[#FF914D]/80 transition-colors mb-2"
                                            data-testid={`toggle-synthesis-${m.id}`}
                                        >
                                            <Sparkles className="w-3.5 h-3.5" />
                                            <span className="font-semibold uppercase tracking-wider">
                                                Dernière synthèse Providence
                                            </span>
                                            <span className="text-muted-foreground normal-case">· {formatDate(m.last_synthesis.created_at)}</span>
                                            {isExpanded
                                                ? <ChevronUp className="w-3.5 h-3.5" />
                                                : <ChevronDown className="w-3.5 h-3.5" />}
                                        </button>
                                        {isExpanded && (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                transition={{ duration: 0.2 }}
                                                className="bg-[#681793]/10 border border-[#681793]/30 rounded-lg p-4 mt-2"
                                            >
                                                <pre className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap font-sans m-0">
                                                    {m.last_synthesis.content || ''}
                                                </pre>
                                            </motion.div>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-xs text-muted-foreground italic">
                                        Aucune synthèse Providence pour le moment — elle sera générée automatiquement après {m.cadence_days || 7} jours.
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    </motion.div>
                );
            })}
        </div>
    );
}
